import { Injectable } from '@angular/core';
import { DataApiService } from './data-api-service';
import { QuantApiService } from '../quant-api/quant-api.service';
import { ConfigService } from 'src/app/service/config.service';
import { Observable, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import {
  ScoreUtilsService,
  ScoreTimeSeriesContentParams,
} from '../../utils/score-utils.service';
import {
  BullBearIndustryBenchItemizedResult,
  BullBearIndustryBenchRequestParams,
  BullBearScorePoint,
  BullBearScoreSeries,
  IndustryBenchItemizedResult,
  IndustryBenchRequestParams,
  IndustryBenchScoreSeries,
  ScoreRequestTypes,
  ScoreTimeSeriesRequestBody,
  ScoreTimeSeriesResponse,
  ScoreTimeSeriesResult,
  ScoreTimeSeriesValue,
} from '../../models/score-time-series.interface';

// Same defaults as ScoreEventsService (MR 5 convention for quant/score requests).
const DEFAULT_LANG = ['en', 'de'];
const DEFAULT_FEED_TYPES = [
  'non_professional',
  'online',
  'print',
  'professional',
];

// Raw quant wire shapes (`score_ts_industry_bench*` keys) remapped below.
interface IndustryBenchWireRow {
  entity_info: { compound_key?: string | null };
  score_ts_industry_bench?: ScoreTimeSeriesValue[];
  score_ts_industry_bench_last_score?: ScoreTimeSeriesValue;
}

/** Optional entity/time scoping for the convenience score fetchers. */
export interface ScoreFetchOptions {
  /** Compound keys (`type:id`); omit to use the current query-param context. */
  entities?: string[];
  /** URL-style time (`'7'` or `'2026-01-01--2026-02-01'`); omit for the default. */
  time?: string;
}

/**
 * Parameters for getScoreTimeSeries method
 *
 * @example Standalone usage (chat-charts component)
 * {
 *   apiOverrides: {},
 *   params: {
 *     type: 'entities',
 *     score_type: 'esg',
 *     entity: ['bmw'],
 *     entityType: 'company',
 *     time: '2025-07-24--2025-10-24'
 *   }
 * }
 *
 * @example Query params usage (like news implementation)
 * {
 *   apiOverrides: {},
 *   params: {
 *     type: 'entities',
 *     score_type: 'esg'
 *     // entity and time resolved from URL query params
 *   }
 * }
 *
 * @example With API overrides
 * {
 *   apiOverrides: { collapse_on: ['participant'] },
 *   params: { type: 'entities', score_type: 'esg' }
 * }
 */
export interface ScoreTimeSeriesServiceParams {
  /**
   * Optional API fields to override in the final request
   * Use for fields like collapse_on or to override computed values
   */
  apiOverrides?: Partial<ScoreTimeSeriesRequestBody>;

  /**
   * Content params for building the request
   * - Explicit values (entity, time) → uses them
   * - No values → falls back to query params
   */
  params: ScoreTimeSeriesContentParams;
}

// Legacy alias for backward compatibility
export interface ScoreTimeSeriesParams {
  customParams: Partial<ScoreTimeSeriesRequestBody>;
  body: ScoreTimeSeriesContentParams;
}

@Injectable({
  providedIn: 'root',
})
export class ScoreTimeSeriesService {
  constructor(
    private dataApiService: DataApiService,
    private scoreRequestUtil: ScoreUtilsService,
    private quantApiService: QuantApiService,
    private config: ConfigService,
  ) {}

  /**
   * Fetches score time series data from v2/api/score_ts
   */
  getScoreTimeSeries(
    params: ScoreTimeSeriesParams,
  ): Observable<ScoreTimeSeriesResponse> {
    const requestBody = this.scoreRequestUtil.createScoreTimeSeriesRequest(
      params.customParams,
      params.body,
    );
    return this.dataApiService
      .post<ScoreTimeSeriesResponse>('score_ts', requestBody, {}, 'v2')
      .pipe(switchMap((resp) => of(JSON.parse(JSON.stringify(resp)))));
  }

  /**
   * Fetches `score_ts` collapsed on participant and returns the single aggregated
   * series (`score_ts[0].score_ts`). The canonical way to get one combined line
   * for an entity/portfolio. Errors degrade to `[]`.
   */
  fetchCollapsedSeries(
    scoreType: ScoreRequestTypes,
    opts: ScoreFetchOptions = {},
  ): Observable<ScoreTimeSeriesValue[]> {
    return this.getScoreTimeSeries({
      customParams: { collapse_on: ['participant'] },
      body: { score_type: scoreType, entity: opts.entities, time: opts.time },
    }).pipe(
      map((response) => response.score_ts?.[0]?.score_ts ?? []),
      catchError(() => of([] as ScoreTimeSeriesValue[])),
    );
  }

  /**
   * Fetches `score_ts` per entity (no `collapse_on`) and returns the result rows,
   * one per participant. Callers key by `entity_info.compound_key`. Errors degrade
   * to `[]`.
   */
  fetchEntityResults(
    scoreType: ScoreRequestTypes,
    opts: ScoreFetchOptions = {},
  ): Observable<ScoreTimeSeriesResult[]> {
    return this.getScoreTimeSeries({
      customParams: {},
      body: { score_type: scoreType, entity: opts.entities, time: opts.time },
    }).pipe(
      map((response) => response.score_ts ?? []),
      catchError(() => of([] as ScoreTimeSeriesResult[])),
    );
  }

  // --- Industry-bench score series (quant `trading` / `trading/v3`) ---

  /** GET `trading/{type}/{id}/score_ts_industry_bench`, remapped to `{score_ts, score_ts_last_score}`. */
  getEntityIndustryBenchScore(
    entityType: string,
    entityId: string,
    params: IndustryBenchRequestParams,
  ): Observable<IndustryBenchScoreSeries> {
    return this.quantApiService
      .get<IndustryBenchWireRow>(
        `${entityType}/${entityId}/score_ts_industry_bench`,
        this.buildIndustryBenchQueryParams(params),
        'trading',
      )
      .pipe(
        map((respArg) => {
          const resp = JSON.parse(JSON.stringify(respArg));
          if (
            !Object.prototype.hasOwnProperty.call(
              resp,
              'score_ts_industry_bench',
            )
          ) {
            return resp as IndustryBenchScoreSeries;
          }
          return {
            score_ts: resp.score_ts_industry_bench,
            score_ts_last_score: resp.score_ts_industry_bench_last_score,
          } as IndustryBenchScoreSeries;
        }),
      );
  }

  /** POST `trading/portfolio/score_ts_industry_bench_itemized`; rows aligned to the posted portfolio order. */
  getPortfolioIndustryBenchScoreItemized(
    portfolioContent: string[],
    params: IndustryBenchRequestParams,
  ): Observable<IndustryBenchItemizedResult[]> {
    if (!portfolioContent.length) {
      return of([]);
    }
    return this.quantApiService
      .post<{
        score_ts_industry_bench_itemized: IndustryBenchWireRow[];
      }>(
        'portfolio/score_ts_industry_bench_itemized',
        portfolioContent,
        this.buildIndustryBenchQueryParams(params),
        'trading',
      )
      .pipe(
        map((respArg) => {
          const resp = JSON.parse(JSON.stringify(respArg));
          const itemized: IndustryBenchWireRow[] | undefined =
            resp.score_ts_industry_bench_itemized;
          if (!itemized) {
            throw new Error('Error: wrong format of the response.');
          }
          if (
            !itemized[0] ||
            !Object.prototype.hasOwnProperty.call(
              itemized[0],
              'score_ts_industry_bench',
            )
          ) {
            return itemized as unknown as IndustryBenchItemizedResult[];
          }
          return this.orderByPayload(itemized, portfolioContent).map((el) => ({
            entity_info: el.entity_info as { compound_key: string },
            score_ts: el.score_ts_industry_bench,
            score_ts_last_score: el.score_ts_industry_bench_last_score,
          }));
        }),
      );
  }

  /** GET `trading/v3/{type}/{id}/bull_bear_score_ts_industry_bench`, points enriched with bull/bear trend fields. */
  getEntityBullBearIndustryBenchScore(
    entityType: string,
    entityId: string,
    params: BullBearIndustryBenchRequestParams,
  ): Observable<BullBearScoreSeries> {
    return this.quantApiService
      .get<IndustryBenchWireRow>(
        `${entityType}/${entityId}/bull_bear_score_ts_industry_bench`,
        this.buildBullBearIndustryBenchQueryParams(params),
        'trading/v3',
      )
      .pipe(
        map((respArg) => {
          const resp = JSON.parse(JSON.stringify(respArg));
          const series: ScoreTimeSeriesValue[] | undefined =
            resp.score_ts_industry_bench;
          if (!series?.length) {
            return resp as BullBearScoreSeries;
          }
          const points = series.map((el) => this.toBullBearPoint(el));
          return {
            score_ts: points,
            score_ts_last_score: points[points.length - 1],
          };
        }),
      );
  }

  /** POST `trading/v3/portfolio/bull_bear_score_ts_industry_bench_itemized`; missing entities yield `undefined` rows. */
  getPortfolioBullBearIndustryBenchScoreItemized(
    portfolioContent: string[],
    params: BullBearIndustryBenchRequestParams,
  ): Observable<BullBearIndustryBenchItemizedResult[]> {
    if (!portfolioContent.length) {
      return of([]);
    }
    return this.quantApiService
      .post<{
        score_ts_industry_bench_itemized: IndustryBenchWireRow[];
      }>(
        'portfolio/bull_bear_score_ts_industry_bench_itemized',
        portfolioContent,
        this.buildBullBearIndustryBenchQueryParams(params),
        'trading/v3',
      )
      .pipe(
        map((respArg) => {
          const resp = JSON.parse(JSON.stringify(respArg));
          const itemized: IndustryBenchWireRow[] | undefined =
            resp.score_ts_industry_bench_itemized;
          if (
            !itemized?.[0] ||
            !Object.prototype.hasOwnProperty.call(
              itemized[0],
              'score_ts_industry_bench',
            )
          ) {
            return resp as BullBearIndustryBenchItemizedResult[];
          }
          return this.orderByPayload(itemized, portfolioContent).map((el) => {
            if (!el.score_ts_industry_bench?.length) {
              return undefined;
            }
            const points = el.score_ts_industry_bench.map((el2) =>
              this.toBullBearPoint(el2),
            );
            return {
              entity_info: el.entity_info as { compound_key: string },
              score_ts: points,
              score_ts_last_score: points[points.length - 1],
            };
          });
        }),
      );
  }

  /** Aligns itemized rows to the posted portfolio order; missing entities become bare `entity_info` placeholders. */
  private orderByPayload(
    rows: IndustryBenchWireRow[],
    payload: string[],
  ): IndustryBenchWireRow[] {
    return payload.map((compoundKey) => {
      const match = rows.find(
        (item) => item.entity_info.compound_key === compoundKey,
      );
      return match
        ? { ...match, entity_info: { compound_key: compoundKey } }
        : { entity_info: { compound_key: compoundKey } };
    });
  }

  private toBullBearPoint(el: ScoreTimeSeriesValue): BullBearScorePoint {
    const trend = ScoreUtilsService.getBullBearTrend(el.score);
    return {
      date: el.date,
      score: el.score,
      trend_type: trend.trendType,
      trend_value: trend.trendValue,
    };
  }

  private buildIndustryBenchQueryParams(
    params: IndustryBenchRequestParams,
  ): Record<string, unknown> {
    const queryParams: Record<string, unknown> = {
      project_id: this.config.appConfig.routing.apiproj,
      from: params.dateFrom,
      to: params.dateTo,
      lang: params.lang ?? DEFAULT_LANG,
      feed_types: params.feedTypes ?? DEFAULT_FEED_TYPES,
    };

    if (params.eventWeights && Object.keys(params.eventWeights).length) {
      Object.assign(queryParams, params.eventWeights);
    } else if (params.scoreType) {
      queryParams['score_type'] = params.scoreType;
    }

    if (params.withBlackSwan) {
      queryParams['with_black_swan'] = true;
    }
    if (params.disableIndustryMateriality) {
      queryParams['disable_industry_materiality'] = true;
    }
    if (params.riskSensitivity !== undefined) {
      queryParams['risk_sensitivity'] = params.riskSensitivity;
    }

    return queryParams;
  }

  private buildBullBearIndustryBenchQueryParams(
    params: BullBearIndustryBenchRequestParams,
  ): Record<string, unknown> {
    return {
      project_id: this.config.appConfig.routing.apiproj,
      from: params.dateFrom,
      to: params.dateTo,
      time_horizon: params.timeHorizon,
      lang: params.lang ?? DEFAULT_LANG,
      feed_types: params.feedTypes ?? DEFAULT_FEED_TYPES,
    };
  }
}
